import { useEffect, useMemo } from "react";
import { ReportFilters } from "@common/types/filters/reports";
import { AccountingReportCycle } from "@common/constants";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import { useEntitiesContexts } from "@hooks/useEntitiesContexts";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useReportParams } from "./useReportParams";

function getDefaultThisYearToDateFilters(): Omit<ReportFilters, "entityIds"> {
    const today = new Date();
    return {
        start: DATE_RANGE_PRESETS_BY_ID.this_year_to_date.getStart(today),
        end: DATE_RANGE_PRESETS_BY_ID.this_year_to_date.getEnd(today),
        cycle: AccountingReportCycle.MONTH,
    };
}

export function useDefaultReportsFilters(): ReportFilters {
    const { entityId, cycle, start, end } = useReportParams();

    const entitiesContexts = useEntitiesContexts({
        onlyBusiness: true,
    });
    const { error } = useToaster();
    const firstEntity = entitiesContexts?.[0];
    const selectedEntity = entityId
        ? entitiesContexts?.find((ctx) => ctx.entity.id === entityId)
        : firstEntity;

    useEffect(() => {
        if (entitiesContexts && !selectedEntity) {
            error("Entity not found");
        }
    }, [error, selectedEntity, entitiesContexts]);

    const entityToUse = selectedEntity ?? firstEntity;

    return useMemo(() => {
        const withoutParams = {
            ...getDefaultThisYearToDateFilters(),
            entityIds: entityToUse ? [entityToUse.entity.id] : [],
        };

        return {
            ...withoutParams,
            cycle: cycle ?? withoutParams.cycle,
            start: start ?? withoutParams.start,
            end: end ?? withoutParams.end,
        };
    }, [cycle, end, entityToUse, start]);
}
